import { SearchOption, TypeaheadInput, TypeaheadInputComponent } from "@jsq/ds/core";
import { withClientContext } from "~/apollo/client";
import { useGetEntitiesWithSearchLazyQuery } from "~/graphql/mainHooks";
import { EntityOption } from "../../CaseInformationCard/types";

interface Props {
  name?: string;
  label?: string;
  readOnly?: boolean;
  placeholder?: string;
  initialSearchValue?: string;
  arenaId?: string;
  onSelectOption?: (selectedOption: EntityOption) => void;
  onClearValue?: () => void;
  entityTypeaheadInputRef?: React.RefObject<TypeaheadInputComponent>;
}

const EntitySelector = (props: Props) => {
  /**
   * This represents the queries to get the list of entities.
   * The search term will be updated by the Typeahead code.
   */
  const [getEntitiesWithSearch, { loading, error, data }] = useGetEntitiesWithSearchLazyQuery({
    variables: {
      arenaId: undefined,
      searchTerm: "",
      filterEntitiesWithTransactions: false,
    },
    fetchPolicy: "no-cache",
    context: withClientContext("staff"),
  });

  /**
   * This is where the list is populated from. It's populated from the above query.
   * Importantly the label shown here is the name of the entity and what will be visible to
   * the user. The ID is a global ID of the associated entity and can be used by callers.
   */
  const entityOptions: SearchOption[] =
    data?.investmentEntitiesByArena?.edges?.map((obj) => ({
      id: obj?.node?.id,
      label: obj?.node?.name,
      type: "account",
    })) ?? [];

  return (
    <TypeaheadInput
      css={{ width: "225px" }}
      name={props.name}
      label={props.label}
      readOnly={props.readOnly || !props.arenaId}
      placeholder={props.placeholder}
      initialSearchValue={props.initialSearchValue}
      className="stacked-md"
      options={entityOptions}
      isLoading={loading}
      isError={error !== undefined}
      onDebouncedSearch={(searchTerm: string) => {
        void getEntitiesWithSearch({
          variables: {
            searchTerm: searchTerm.trim(),
            arenaId: props.arenaId,
            filterEntitiesWithTransactions: false,
          },
        });
      }}
      onSelectOption={(selectedOption: SearchOption) => {
        const program = data?.investmentEntitiesByArena?.edges
          ?.map((e) => e?.node)
          .filter((node) => node.id === selectedOption.id)?.[0]
          .complianceSettings?.complianceProgram;

        props.onSelectOption?.({
          id: selectedOption.id.toString(),
          label: selectedOption.label,
          program: program?.toString(),
        });
      }}
      onClearValue={props.onClearValue}
      ref={props.entityTypeaheadInputRef}
    ></TypeaheadInput>
  );
};
export default EntitySelector;
